import React, { useState, useEffect, useRef } from 'react';
import { useSetCurrentPage } from '../stores/useAppStore';
import { useKlineMonitorStore } from '../stores/useKlineMonitorStore';
import GradientBackground from '../components/ui/GradientBackground';
import ParticleBackground from '../components/ui/ParticleBackground';
import ContentDialog from '../components/ContentDialog';
import KlineAIMonitorDialog from '../components/KlineAIMonitorDialog';
import ConfirmDialog from '../components/ConfirmDialog';
import { klineDataService } from '../services/klineDataService';
import { klineMonitorService } from '../services/klineMonitorService';
import { useToastStore } from '../stores/useToastStore';
import { BarInterval, RankingAlgorithm } from '@/services/enums';
import { adminService, SystemPromptInfo } from '../services/adminService';
import { useEffectOnce } from '../hooks/useEffectOnce';

// Components
import MonitorCard from '../components/MyMonitors/MonitorCard';
import FilterBar from '../components/MyMonitors/FilterBar';
import MonitorStatusHeader from '../components/MyMonitors/MonitorStatusHeader';
import AutoMonitorDialog from '../components/MyMonitors/AutoMonitorDialog';
import MonitorLogsDialog from '../components/MyMonitors/MonitorLogsDialog';
import { estimateTokenCount } from '../components/MyMonitors/utils';
import PageHeader from '../components/PageHeader';
import { MonitorFilterStatus } from '@/services/enums';

type StatusFilter = MonitorFilterStatus;

const MyMonitors: React.FC = () => {
  // 页面状态
  const [loading, setLoading] = useState(true);
  const [symbolFilter, setSymbolFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [autoMonitorRunning, setAutoMonitorRunning] = useState(false);
  const [autoMonitorLoading, setAutoMonitorLoading] = useState(false);
  const [showAutoMonitorDialog, setShowAutoMonitorDialog] = useState(false);
  const [batchOperating, setBatchOperating] = useState(false);

  // 系统数据
  const [systemPrompts, setSystemPrompts] = useState<SystemPromptInfo[]>([]);
  const [loadingPrompts, setLoadingPrompts] = useState(false);

  // Store hooks
  const setCurrentPage = useSetCurrentPage();
  const {
    monitors,
    isLoading,
    error,
    refreshMonitors,
    stopMonitor,
    startMonitor,
    stoppingMonitors,
    startingMonitors,
    updateMonitorStatus,
    clearError,
    silentRefreshMonitors,
    deleteStoppedMonitors,
    getAutoMonitorStatus,
  } = useKlineMonitorStore();

  const showToast = useToastStore((state) => state.show);
  const hasInitializedRef = useRef(false);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const silentRefreshIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // 内容展示弹窗状态
  const [dialogVisible, setDialogVisible] = useState(false);
  const [dialogTitle, setDialogTitle] = useState('');
  const [dialogContent, setDialogContent] = useState('');
  const [dialogSystemPrompt, setDialogSystemPrompt] = useState<string | undefined>(undefined);
  const [dialogUserPrompt, setDialogUserPrompt] = useState<string | undefined>(undefined);
  const [loadingContent, setLoadingContent] = useState(false);
  const [dialogTokenCount, setDialogTokenCount] = useState<number | undefined>(undefined);
  const [dialogMonitorInfo, setDialogMonitorInfo] = useState<{
    monitorId: string;
    symbol: string;
    bar: BarInterval;
    promptHash: string;
  } | null>(null);

  // 详情/编辑对话框状态
  const [showMonitorDialog, setShowMonitorDialog] = useState(false);
  const [selectedMonitor, setSelectedMonitor] = useState<{
    symbol: string;
    bar: BarInterval;
    enableAutoTrading?: boolean;
  } | null>(null);

  // 批量删除确认对话框状态
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [stoppedCount, setStoppedCount] = useState(0);

  // 监控日志对话框状态
  const [showLogsDialog, setShowLogsDialog] = useState(false);

  // 加载系统提示词列表
  useEffectOnce(() => {
    const loadSystemPrompts = async () => {
      try {
        setLoadingPrompts(true);
        const result = await adminService.getSystemPrompts(1, 100, undefined, true);
        setSystemPrompts(result.prompts);
      } catch (error: any) {
        console.error('加载系统提示词列表失败:', error);
      } finally {
        setLoadingPrompts(false);
      }
    };
    loadSystemPrompts();
  });

  // 检查自主监控服务状态
  const refreshAutoMonitorStatus = async () => {
    try {
      const data = await getAutoMonitorStatus();
      setAutoMonitorRunning(data.isRunning);
    } catch (error) {
      // 忽略错误
    }
  };

  // 初始加载
  useEffectOnce(() => {
    hasInitializedRef.current = true;
    refreshMonitors().finally(() => setLoading(false));
    refreshAutoMonitorStatus();
  });

  // 静默刷新逻辑
  useEffect(() => {
    const startSilentRefresh = () => {
      const filterValue = symbolFilter.trim() || undefined;
      const statusValue = statusFilter === 'all' ? undefined : statusFilter;
      silentRefreshMonitors(filterValue, statusValue).catch(() => {});
    };

    if (hasInitializedRef.current) {
      const initialTimeout = setTimeout(() => {
        startSilentRefresh();
      }, 5000);

      silentRefreshIntervalRef.current = setInterval(startSilentRefresh, 30000);

      return () => {
        clearTimeout(initialTimeout);
        if (silentRefreshIntervalRef.current) {
          clearInterval(silentRefreshIntervalRef.current);
          silentRefreshIntervalRef.current = null;
        }
      };
    }
  }, [symbolFilter, statusFilter, silentRefreshMonitors]);

  // 筛选防抖
  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    if (hasInitializedRef.current) {
      setLoading(true);
      debounceTimerRef.current = setTimeout(() => {
        const filterValue = symbolFilter.trim() || undefined;
        const statusValue = statusFilter === 'all' ? undefined : statusFilter;
        refreshMonitors(filterValue, statusValue).finally(() => setLoading(false));
      }, 500);
    }

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symbolFilter, refreshMonitors]);

  // 状态筛选直接刷新
  useEffect(() => {
    if (hasInitializedRef.current) {
      setLoading(true);
      const filterValue = symbolFilter.trim() || undefined;
      const statusValue = statusFilter === 'all' ? undefined : statusFilter;
      refreshMonitors(filterValue, statusValue).finally(() => setLoading(false));
    }
  }, [statusFilter, symbolFilter, refreshMonitors]);

  // 处理停止监控
  const handleStopMonitor = async (monitorId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    await stopMonitor(monitorId);
  };

  // 处理启动监控
  const handleStartMonitor = async (monitorId: string, symbol: string, bar: BarInterval, e: React.MouseEvent) => {
    e.stopPropagation();
    await startMonitor(monitorId, symbol, bar);
  };

  // 处理查看提示词
  const handleViewPrompt = async (
    monitor: { monitorId: string; symbol: string; bar: BarInterval; promptHash: string },
    e: React.MouseEvent,
  ) => {
    e.stopPropagation();
    if (!monitor.promptHash) {
      showToast('该监控任务没有提示词', 'error');
      return;
    }

    setLoadingContent(true);
    setDialogVisible(true);
    setDialogTitle('提示词');
    setDialogContent('加载中...');
    setDialogTokenCount(undefined);
    setDialogMonitorInfo(monitor);

    try {
      const result = await klineDataService.getPrompt(monitor.promptHash, monitor.bar);
      setDialogSystemPrompt(result.systemPrompt);
      setDialogUserPrompt(result.userPrompt);
      const fullPrompt = result.systemPrompt
        ? `${result.systemPrompt}\n\n----------\n\n${result.userPrompt}`
        : result.userPrompt;
      setDialogContent(fullPrompt);
      const tokenCount = estimateTokenCount(fullPrompt);
      setDialogTokenCount(tokenCount);
    } catch (error: any) {
      showToast(error.message || '获取提示词失败', 'error');
      setDialogVisible(false);
      setDialogMonitorInfo(null);
    } finally {
      setLoadingContent(false);
    }
  };

  // 处理查看分析报告
  const handleViewAnalysisReport = async (analysisReportHash: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!analysisReportHash) {
      showToast('该监控任务没有分析报告', 'error');
      return;
    }

    setLoadingContent(true);
    setDialogVisible(true);
    setDialogTitle('AI分析报告');
    setDialogContent('加载中...');

    try {
      const result = await klineDataService.getAnalysisReport(analysisReportHash);
      setDialogContent(result.analysisReport);
    } catch (error: any) {
      showToast(error.message || '获取分析报告失败', 'error');
      setDialogVisible(false);
    } finally {
      setLoadingContent(false);
    }
  };

  // 处理批量启动
  const handleBatchStart = async () => {
    const stoppedMonitors = monitors.filter((m) => m.status === 'stopped');
    if (stoppedMonitors.length === 0) return;

    setBatchOperating(true);
    let successCount = 0;
    let failCount = 0;

    for (const monitor of stoppedMonitors) {
      try {
        await startMonitor(monitor.monitorId, monitor.symbol, monitor.bar);
        successCount++;
        await new Promise((resolve) => setTimeout(resolve, 200));
      } catch (error: any) {
        failCount++;
        console.error(`批量启动监控失败: ${monitor.symbol} ${monitor.bar}`, error);
      }
    }

    setBatchOperating(false);
    if (failCount === 0) {
      showToast(`成功启动 ${successCount} 个监控任务`, 'success');
    } else {
      showToast(
        `启动完成：成功 ${successCount} 个，失败 ${failCount} 个`,
        failCount === stoppedMonitors.length ? 'error' : 'warning',
      );
    }
  };

  // 处理批量停止
  const handleBatchStop = async () => {
    const runningMonitors = monitors.filter((m) => m.status === 'running');
    if (runningMonitors.length === 0) return;

    setBatchOperating(true);
    let successCount = 0;
    let failCount = 0;

    for (const monitor of runningMonitors) {
      try {
        await stopMonitor(monitor.monitorId);
        successCount++;
        await new Promise((resolve) => setTimeout(resolve, 200));
      } catch (error: any) {
        failCount++;
        console.error(`批量停止监控失败: ${monitor.symbol} ${monitor.bar}`, error);
      }
    }

    setBatchOperating(false);
    if (failCount === 0) {
      showToast(`成功停止 ${successCount} 个监控任务`, 'success');
    } else {
      showToast(
        `停止完成：成功 ${successCount} 个，失败 ${failCount} 个`,
        failCount === runningMonitors.length ? 'error' : 'warning',
      );
    }
  };

  // 处理批量删除
  const handleBatchDelete = async () => {
    const count = monitors.filter((m) => m.status === 'stopped').length;
    if (count === 0) return;
    setStoppedCount(count);
    setShowDeleteConfirm(true);
  };

  const confirmBatchDelete = async () => {
    setBatchOperating(true);
    try {
      await deleteStoppedMonitors();
      await refreshMonitors();
      setShowDeleteConfirm(false);
    } catch (error: any) {
      console.error('批量删除监控失败:', error);
    } finally {
      setBatchOperating(false);
    }
  };

  // 处理自主监控按钮点击
  const handleAutoMonitorClick = async () => {
    if (autoMonitorRunning) {
      setAutoMonitorLoading(true);
      try {
        await klineMonitorService.stopAutoMonitor();
        setAutoMonitorRunning(false);
        showToast('自主监控已停止', 'success');
      } catch (error: any) {
        showToast(error.message || '停止自主监控失败', 'error');
      } finally {
        setAutoMonitorLoading(false);
      }
    } else {
      setShowAutoMonitorDialog(true);
    }
  };

  // 处理确认启动自主监控
  const handleConfirmAutoMonitor = async (config: {
    enableAutoTrading: boolean;
    monitorCount: number;
    minVolume: number;
    excludedSymbols: string[];
    systemPromptId?: string;
    tradingParams: {
      leverage: number;
      positionSize: number;
      takeProfitPercent: number;
      stopLossPercent: number;
    };
    rankingAlgorithm: RankingAlgorithm;
    timeFrame: BarInterval;
  }) => {
    setShowAutoMonitorDialog(false);
    setAutoMonitorLoading(true);
    try {
      await klineMonitorService.startAutoMonitor(
        config.enableAutoTrading,
        config.monitorCount,
        config.excludedSymbols,
        config.systemPromptId,
        config.tradingParams,
        config.minVolume,
        config.rankingAlgorithm,
        config.timeFrame,
      );
      setAutoMonitorRunning(true);
      showToast('自主监控已启动', 'success');
    } catch (error: any) {
      showToast(error.message || '启动自主监控失败', 'error');
    } finally {
      setAutoMonitorLoading(false);
    }
  };

  return (
    <GradientBackground className="text-white pb-20">
      <div className="min-h-screen">
        <ParticleBackground />

        {/* 顶部标题栏 */}
        <PageHeader
          title="我的监控"
          onBack={() => setCurrentPage('personal-center')}
          rightContent={
            <button
              onClick={() => {
                refreshMonitors();
                refreshAutoMonitorStatus();
              }}
              disabled={isLoading}
              className="w-9 h-9 flex items-center justify-center bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl text-white hover:bg-white/20 hover:border-white/30 transition-all duration-300 hover:scale-105 disabled:opacity-50"
            >
              <svg
                className={`w-5 h-5 ${isLoading ? 'animate-spin' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
            </button>
          }
        />

        {/* 筛选栏 */}
        <FilterBar
          symbolFilter={symbolFilter}
          setSymbolFilter={setSymbolFilter}
          statusFilter={statusFilter}
          setStatusFilter={setStatusFilter}
          autoMonitorRunning={autoMonitorRunning}
          autoMonitorLoading={autoMonitorLoading}
          onAutoMonitorClick={handleAutoMonitorClick}
        />

        {/* 内容区域 */}
        <div className="px-4 pt-28 pb-4">
          <MonitorStatusHeader
            monitors={monitors}
            isLoading={isLoading}
            batchOperating={batchOperating}
            startingMonitors={startingMonitors}
            stoppingMonitors={stoppingMonitors}
            onBatchStart={handleBatchStart}
            onBatchStop={handleBatchStop}
            onBatchDelete={handleBatchDelete}
            onViewLogs={() => setShowLogsDialog(true)}
          />

          {error && (
            <div className="mb-4 p-3 bg-red-500/20 border border-red-500/50 rounded-lg">
              <div className="text-red-300 text-sm">{error}</div>
              <button onClick={clearError} className="mt-2 text-red-300 text-xs underline">
                关闭
              </button>
            </div>
          )}

          {loading || isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
            </div>
          ) : monitors.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-gray-400 mb-2">暂无监控任务</div>
              <button
                onClick={() => setCurrentPage('watchlist')}
                className="mt-4 px-4 py-2 bg-blue-500/20 text-blue-300 border border-blue-500/50 rounded-lg hover:bg-blue-500/30 transition-colors"
              >
                去创建监控
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {monitors.map((monitor) => (
                <MonitorCard
                  key={monitor.monitorId}
                  monitor={monitor}
                  isStopping={stoppingMonitors.has(monitor.monitorId)}
                  isStarting={startingMonitors.has(monitor.monitorId)}
                  loadingContent={loadingContent}
                  onStop={handleStopMonitor}
                  onStart={handleStartMonitor}
                  onViewPrompt={handleViewPrompt}
                  onViewReport={handleViewAnalysisReport}
                  onClick={(m) => {
                    setSelectedMonitor({
                      symbol: m.symbol,
                      bar: m.bar as BarInterval,
                      enableAutoTrading: m.enableAutoTrading || false,
                    });
                    setShowMonitorDialog(true);
                  }}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* 内容展示弹窗 */}
      <ContentDialog
        visible={dialogVisible}
        title={dialogTitle}
        content={dialogContent}
        onClose={() => {
          setDialogVisible(false);
          setDialogSystemPrompt(undefined);
          setDialogUserPrompt(undefined);
          setDialogMonitorInfo(null);
        }}
        useMarkdown={dialogTitle === 'AI分析报告' || dialogTitle === '提示词'}
        tokenCount={dialogTokenCount}
        systemPrompt={dialogSystemPrompt}
        userPrompt={dialogUserPrompt}
        monitorInfo={dialogMonitorInfo}
      />

      {/* 批量删除确认对话框 */}
      <ConfirmDialog
        show={showDeleteConfirm}
        title="确认批量删除"
        message={`确定要删除所有 ${stoppedCount} 个停止运行的监控任务吗？此操作不可恢复，请谨慎操作。`}
        confirmText="确认删除"
        cancelText="取消"
        confirmButtonClass="bg-orange-500/20 text-orange-300 border-orange-500/50 hover:bg-orange-500/30"
        loading={batchOperating}
        onConfirm={confirmBatchDelete}
        onCancel={() => setShowDeleteConfirm(false)}
      />

      {/* 监控对话框 */}
      {showMonitorDialog && selectedMonitor && (
        <KlineAIMonitorDialog
          symbol={selectedMonitor.symbol}
          bar={selectedMonitor.bar}
          enableAutoTrading={selectedMonitor.enableAutoTrading}
          onClose={(stateChange) => {
            setShowMonitorDialog(false);
            setSelectedMonitor(null);
            if (stateChange) {
              updateMonitorStatus(
                selectedMonitor.symbol,
                selectedMonitor.bar,
                stateChange.status,
                stateChange.monitorId,
              );
            }
          }}
        />
      )}

      {/* 自主监控配置对话框 */}
      <AutoMonitorDialog
        isOpen={showAutoMonitorDialog}
        onClose={() => setShowAutoMonitorDialog(false)}
        onConfirm={handleConfirmAutoMonitor}
        loading={autoMonitorLoading}
        systemPrompts={systemPrompts}
        loadingPrompts={loadingPrompts}
      />

      {/* 监控日志对话框 */}
      <MonitorLogsDialog isOpen={showLogsDialog} onClose={() => setShowLogsDialog(false)} />
    </GradientBackground>
  );
};

export default MyMonitors;
